<template>
  <div class="radio-button-holder">
    <input
      type="radio"
      :id="id"
      :data-qa="dataQa"
      :class="['radio-button', classes]"
      :name="name"
      :disabled="isDisabled"
      :checked="checked"
      @input="handleInput($event)"
    />
    <label :for="id" class="radio-label rbz-text-regular">
      {{ label }}
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface FormRadioProps {
  id: string;
  label?: string;
  disabled?: boolean;
  checked?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  name?: string;
  classes?: string;
  dataQa?: string;
}

const props = withDefaults(defineProps<FormRadioProps>(), {
  label: '',
  disabled: false,
  name: 'radio',
  classes: '',
  dataQa: '',
});

interface FormRadioEmits {
  (e: 'input'): void;
}

const emit = defineEmits<FormRadioEmits>();

const handleInput = (event: Event) => {
  emit('input');
  clearFocus(event);
};

const isDisabled = computed(() => {
  return props.disabled || (props.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const clearFocus = (event: Event) => {
  if (event.pointerType === 'mouse') {
    (event.currentTarget as HTMLElement)?.blur();
  }
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.radio-button-holder {
  align-items: center;
  display: flex;
  gap: 4px;
  width: fit-content;

  .radio-button {
    appearance: none;
    background-color: map.get(colors.$colors, 'white');
    border: 1.5px solid map.get(colors.$colors, 'dusty-gray');
    border-radius: 50%;
    cursor: pointer;
    display: inline-block;
    height: 16px;
    margin: 4px;
    position: relative;
    width: 16px;
  }

  .radio-button:hover {
    background-color: map.get(colors.$colors, 'gallery');
    border: 1.5px solid map.get(colors.$colors, 'dusty-gray');
  }

  .radio-button:focus:not(:active) {
    background-color: map.get(colors.$colors, 'white');
    border: 1.5px solid map.get(colors.$colors, 'dusty-gray');
    box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
  }

  .radio-button:active {
    background-color: map.get(colors.$colors, 'white');
    border: 1.5px solid map.get(colors.$colors, 'shuttle-gray');

    + .radio-label {
      color: map.get(colors.$colors, 'shuttle-gray');
    }
  }

  .radio-button:disabled {
    background-color: map.get(colors.$colors, 'silver');
    border: 1.5px solid map.get(colors.$colors, 'silver');
    pointer-events: none;

    + .radio-label {
      color: map.get(colors.$colors, 'silver');
    }
  }

  .radio-button:checked {
    border: 1.5px solid map.get(colors.$colors, 'jewel');

    &::after {
      background-color: map.get(colors.$colors, 'jewel');
      border-radius: 50%;
      content: '';
      display: inline-block;
      height: 8px;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
    }

    &:hover {
      border: 1.5px solid map.get(colors.$colors, 'jewel');
    }

    &:hover::after {
      background-color: map.get(colors.$colors, 'jewel');
    }

    &:focus {
      border: 1.5px solid map.get(colors.$colors, 'jewel');
      box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
    }

    &:focus::after {
      background-color: map.get(colors.$colors, 'jewel');
    }

    &:disabled {
      background-color: map.get(colors.$colors, 'white');
      border: 1.5px solid map.get(colors.$colors, 'silver');
      pointer-events: none;
    }

    &:disabled + .radio-label {
      color: map.get(colors.$colors, 'silver');
    }

    &:disabled::after {
      background-color: map.get(colors.$colors, 'silver');
    }
  }

  .radio-label {
    color: map.get(colors.$colors, 'stratos');

    .radio-button:active + & {
      color: map.get(colors.$colors, 'shuttle-gray');
    }
  }
}
</style>
